import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Settings as SettingsIcon, Mail, Save, Loader2, CheckCircle, AlertCircle, Calculator, RefreshCw, Factory, Tag, Camera, Clock, History, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { getAuthHeaders } = useAuth();
  const [alertEmail, setAlertEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  
  // EMA Status
  const [emaStatus, setEmaStatus] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  
  // Production Alert Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [savingCategories, setSavingCategories] = useState(false);
  
  // Stock Snapshots
  const [snapshots, setSnapshots] = useState([]);
  const [takingSnapshot, setTakingSnapshot] = useState(false);
  const [comparingSnapshots, setComparingSnapshots] = useState(false);

  // Non-Running Preference (stored in localStorage)
  const [hideNonRunning, setHideNonRunning] = useState(
    () => localStorage.getItem('hideNonRunning') === 'true'
  );

  const toggleHideNonRunning = (checked) => {
    setHideNonRunning(checked);
    localStorage.setItem('hideNonRunning', checked ? 'true' : 'false');
    toast.success(checked ? 'Non-running items will be hidden from all reports' : 'All items will be shown in reports');
  };

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [alertsRes, emaRes, categoriesRes, alertCategoriesRes, snapshotsRes] = await Promise.all([
          axios.get(`${API}/settings/alerts`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/ema-status`, { headers: getAuthHeaders() }),
          axios.get(`${API}/categories`, { headers: getAuthHeaders() }),
          axios.get(`${API}/settings/production-alert-categories`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/stock-snapshots?limit=5`, { headers: getAuthHeaders() })
        ]);
        setAlertEmail(alertsRes.data.alert_email || '');
        setConfigured(alertsRes.data.configured || false);
        setEmaStatus(emaRes.data);
        setCategories(categoriesRes.data || []);
        setSelectedCategoryIds(alertCategoriesRes.data.category_ids || []);
        setSnapshots(snapshotsRes.data.snapshots || []);
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [getAuthHeaders]);

  // Save alert email settings
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!alertEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(alertEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/alerts`,
        { alert_email: alertEmail },
        { headers: getAuthHeaders() }
      );
      setConfigured(true);
      toast.success('Alert email settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Recalculate EMA averages
  const handleRecalculateAverages = async () => {
    setRecalculating(true);
    try {
      const response = await axios.post(
        `${API}/admin/recalculate-averages`,
        {},
        { headers: getAuthHeaders() }
      );
      setEmaStatus(prev => ({
        ...prev,
        total_records: response.data.records_computed,
        last_computed: response.data.timestamp
      }));
      toast.success(`Averages recalculated: ${response.data.records_computed.toLocaleString()} records updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate averages');
    } finally {
      setRecalculating(false);
    }
  };

  // Toggle category selection
  const handleCategoryToggle = (categoryId) => {
    setSelectedCategoryIds(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  // Save production alert categories
  const handleSaveCategories = async () => {
    setSavingCategories(true);
    try {
      await axios.put(
        `${API}/settings/production-alert-categories`,
        { category_ids: selectedCategoryIds },
        { headers: getAuthHeaders() }
      );
      toast.success(`Production alert categories saved (${selectedCategoryIds.length} selected)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save categories');
    } finally {
      setSavingCategories(false);
    }
  };

  // Take manual snapshot
  const handleTakeSnapshot = async () => {
    setTakingSnapshot(true);
    try {
      const response = await axios.post(
        `${API}/admin/stock-snapshot`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Snapshot created: ${response.data.items_in_reorder} items in reorder out of ${response.data.total_items}`);
      // Refresh snapshots list
      const snapshotsRes = await axios.get(`${API}/admin/stock-snapshots?limit=5`, { headers: getAuthHeaders() });
      setSnapshots(snapshotsRes.data.snapshots || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create snapshot');
    } finally {
      setTakingSnapshot(false);
    }
  };

  // Compare and send email
  const handleCompareAndEmail = async () => {
    if (!alertEmail) {
      toast.error('Please configure an alert email first');
      return;
    }
    setComparingSnapshots(true);
    try {
      const response = await axios.post(
        `${API}/admin/stock-snapshot/compare`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data.message === "Not enough snapshots for comparison (need at least 2)") {
        toast.info('Need at least 2 snapshots to compare. Take another snapshot first.');
      } else {
        toast.success(`Comparison complete: ${response.data.new_in_reorder_count} new reorder items, ${response.data.resolved_count} resolved`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to compare snapshots');
    } finally {
      setComparingSnapshots(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <SettingsIcon className="w-7 h-7" />
          Settings
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Configure system settings and notifications
        </p>
      </div>

      {/* Non-Running Items Preference */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-slate-500" />
            Non-Running Items
          </CardTitle>
          <CardDescription>
            When enabled, items marked as "non-running" in Item Masters will be hidden from all reports, dashboards, and analytics. This preference is saved to your browser only.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-slate-700">Hide non-running items from all reports</p>
              <p className="text-xs text-slate-500 mt-0.5">Affects Dashboard, Inventory, Orders, Analytics, Production, and Transfer Recommendations</p>
            </div>
            <Switch
              checked={hideNonRunning}
              onCheckedChange={toggleHideNonRunning}
              data-testid="hide-non-running-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Alert Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            Stock Alert Email
          </CardTitle>
          <CardDescription>
            Configure the email address that receives stock alerts from salespeople. 
            When a salesperson submits a stock query, the results will be emailed to this address.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="alertEmail">Alert Email Address</Label>
              <div className="flex gap-2">
                <Input
                  id="alertEmail"
                  type="email"
                  placeholder="alerts@yourcompany.com"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  className="flex-1"
                  data-testid="alert-email-input"
                />
                <Button type="submit" disabled={saving} data-testid="save-alert-email-btn">
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {configured && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg p-3">
                <CheckCircle className="w-4 h-4" />
                <span>Alert email is configured and active</span>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Production Alert Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Factory className="w-5 h-5 text-orange-500" />
            Production Alert Categories
          </CardTitle>
          <CardDescription>
            Select which product categories should be monitored for production alerts.
            Low stock items in these categories will be shown in the Production Program page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {categories.length === 0 ? (
            <div className="text-center py-4 text-slate-500">
              <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No categories found. Create categories first.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {categories.map(category => (
                  <div
                    key={category.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedCategoryIds.includes(category.id)
                        ? 'bg-orange-50 border-orange-300'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleCategoryToggle(category.id)}
                  >
                    <Checkbox
                      checked={selectedCategoryIds.includes(category.id)}
                      onCheckedChange={() => handleCategoryToggle(category.id)}
                      className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    />
                    <span className="text-sm font-medium text-slate-700">{category.name}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-slate-500">
                  {selectedCategoryIds.length} of {categories.length} categories selected
                </p>
                <Button 
                  onClick={handleSaveCategories} 
                  disabled={savingCategories}
                  variant="outline"
                  className="border-orange-200 hover:bg-orange-50"
                  data-testid="save-production-categories-btn"
                >
                  {savingCategories ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Categories
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Daily Stock Snapshot */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-cyan-500" />
            Daily Stock Snapshots
          </CardTitle>
          <CardDescription>
            The system takes a snapshot of stock reorder status daily at 2:00 PM IST.
            It compares with the previous day and emails changes to the alert email above.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Schedule Info */}
          <div className="bg-cyan-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-cyan-600" />
              <span className="font-medium text-cyan-800">Automatic Schedule</span>
            </div>
            <p className="text-sm text-cyan-700">
              Snapshots are taken automatically at <strong>2:00 PM IST</strong> daily.
              The system compares with the previous snapshot and sends an email report showing:
            </p>
            <ul className="text-sm text-cyan-700 mt-2 ml-4 list-disc">
              <li>Items that were NOT in reorder but now ARE (new reorder items)</li>
              <li>Items that WERE in reorder but now are NOT (resolved items)</li>
            </ul>
          </div>

          {/* Manual Actions */}
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleTakeSnapshot} 
              disabled={takingSnapshot}
              variant="outline"
              className="border-cyan-200 hover:bg-cyan-50"
              data-testid="take-snapshot-btn"
            >
              {takingSnapshot ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Camera className="w-4 h-4 mr-2" />
              )}
              Take Snapshot Now
            </Button>
            
            <Button 
              onClick={handleCompareAndEmail} 
              disabled={comparingSnapshots || snapshots.length < 2}
              variant="outline"
              className="border-cyan-200 hover:bg-cyan-50"
              data-testid="compare-snapshot-btn"
            >
              {comparingSnapshots ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Compare & Send Email
            </Button>
          </div>

          {/* Recent Snapshots */}
          {snapshots.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-slate-700 flex items-center gap-2 mb-2">
                <History className="w-4 h-4" />
                Recent Snapshots
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Date</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Time</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Total Items</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">In Reorder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshots.map((snapshot, idx) => (
                      <tr key={snapshot.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                        <td className="px-3 py-2 text-slate-800">{snapshot.snapshot_date}</td>
                        <td className="px-3 py-2 text-slate-600">{snapshot.snapshot_time}</td>
                        <td className="px-3 py-2 text-right text-slate-700">{snapshot.total_items?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-medium text-amber-600">{snapshot.items_in_reorder?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* EMA Average Sales Calculation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-purple-500" />
            Average Sales Calculation (EMA)
          </CardTitle>
          <CardDescription>
            Average sales are computed using an Exponential Moving Average (EMA) formula that gives more weight to recent months.
            This calculation happens automatically after sales data uploads, but you can manually trigger a recalculation here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* EMA Status */}
          {emaStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Total Records</p>
                <p className="text-xl font-bold text-slate-800">{emaStatus.total_records?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Depot-Level</p>
                <p className="text-xl font-bold text-slate-800">{emaStatus.depot_level_count?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Aggregate-Level</p>
                <p className="text-xl font-bold text-slate-800">{emaStatus.aggregate_level_count?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Last Computed</p>
                <p className="text-sm font-medium text-slate-800">
                  {emaStatus.last_computed 
                    ? new Date(emaStatus.last_computed).toLocaleString() 
                    : 'Never'}
                </p>
              </div>
            </div>
          )}

          {/* Recalculate Button */}
          <div className="flex items-center gap-4 pt-2">
            <Button 
              onClick={handleRecalculateAverages} 
              disabled={recalculating}
              variant="outline"
              className="border-purple-200 hover:bg-purple-50"
              data-testid="recalculate-averages-btn"
            >
              {recalculating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recalculating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Recalculate Averages
                </>
              )}
            </Button>
            <p className="text-xs text-slate-500">
              Use this if averages seem outdated or after bulk data imports
            </p>
          </div>

          {/* How EMA works */}
          <div className="bg-blue-50 rounded-lg p-4 mt-4">
            <h4 className="font-medium text-blue-800 mb-2">How EMA is Calculated</h4>
            <p className="text-sm text-blue-700">
              Starting from Jan 2026, the formula computes: <code className="bg-blue-100 px-1 rounded">a1 = (Jan + Feb) / 2</code>, 
              then iteratively: <code className="bg-blue-100 px-1 rounded">a1 = (a1 + NextMonth) / 2</code> for each subsequent month.
              This gives more weight to recent sales data compared to a simple average.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            How Stock Alerts Work
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
            <li>Salespeople can query stock levels from the mobile-friendly Salesperson portal</li>
            <li>They search for items and view current stock across all depots</li>
            <li>If they find low stock items, they can submit an alert</li>
            <li>The alert is automatically emailed to the configured address above</li>
            <li>Admins can review and take action on reported stock issues</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
